import { DeliveryMethod, PurchaseOrderFileFormat, SignVATInvoices, UserService } from 'common';
import { z } from 'zod';
import { CharSetForPhoneAndFax, CharSetForDUNSNumber } from '../../../constants';

/**
 * Splitting the schema into sub-schemas per step allows `superRefine` to run
 * when clicking `Next` instead of having it run at the end on submit.
 */
const AccountAndContactSchema = z
    .object({
        loginEmail: z
            .string({ required_error: 'Login Email is required' })
            .min(1, 'Login Email is required')
            .email('Invalid email address'),
        password: z
            .string({ required_error: 'Password is required' })
            .min(1, 'Password is required')
            .superRefine((input, ctx) => {
                const userService = new UserService();

                const errors = userService.validatePassword({ password: input });

                if (errors.length) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: errors[0],
                    });
                }
            }),
        passwordConfirmation: z
            .string({ required_error: 'Password Confirmation is required' })
            .min(1, 'Password Confirmation is required'),
        firstName: z.string({ required_error: 'First Name is required' }).trim().min(1, 'First Name is required'),
        lastName: z.string({ required_error: 'Last Name is required' }).trim().min(1, 'Last Name is required'),
        title: z
            .string()
            .trim()
            .optional()
            .transform((v) => v || undefined),
        phone: z
            .string({ required_error: 'Phone is required' })
            .trim()
            .min(1, 'Phone is required')
            .regex(CharSetForPhoneAndFax, 'Phone includes invalid characters'),
    })
    .superRefine((data, ctx) => {
        if (data.password !== data.passwordConfirmation) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Password and Password Confirmation do not match',
                path: ['passwordConfirmation'],
            });
        }

        if (data.password) {
            const userService = new UserService();

            const firstName = data.firstName;
            const lastName = data.lastName;
            const username = data.loginEmail.split('@')[0];
            const password = data.password;

            const passwordErrors = userService.validatePassword({
                password,
                stringsToOmit: [firstName, lastName, username],
            });

            if (passwordErrors.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: passwordErrors[0], // NOTE: showing first error due to limited space
                    path: ['password'],
                });
            }
        }
    });

const PODeliverySchema = z
    .object({
        deliveryMethod: z.nativeEnum(DeliveryMethod, {
            errorMap: () => ({ message: 'Delivery Method is required' }),
        }),
        deliveryEmail: z
            .union([z.string().email('Invalid email address').trim(), z.string().length(0)])
            .optional()
            .transform((v) => v ?? ''),
        deliveryFileFormat: z.nativeEnum(PurchaseOrderFileFormat, {
            errorMap: () => ({ message: 'File Format is required' }),
        }),
    })
    .superRefine(({ deliveryMethod, deliveryEmail }, ctx) => {
        if (deliveryMethod !== DeliveryMethod.Email) {
            return;
        }

        if (!deliveryEmail?.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Email is required',
                path: ['deliveryEmail'],
            });
        }
    });

const VATSchema = z
    .object({
        VATComplianceRequired: z.boolean().optional().default(false),
        VATNumber: z
            .string({ required_error: 'VAT Registration Number is required' })
            .trim()
            .min(1, 'VAT Registration Number is required')
            .optional()
            .transform((v) => v ?? null),
        SignVATInvoices: z.nativeEnum(SignVATInvoices).optional().default(SignVATInvoices.Both),
    })
    .superRefine(({ VATComplianceRequired, VATNumber }, ctx) => {
        if (VATComplianceRequired && !VATNumber) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'VAT Registration Number is required',
                path: ['VATNumber'],
            });
        }
    });

const YourCustomerSchema = z.object({
    invoiceCurrency: z
        .number({
            errorMap: () => ({ message: 'Invoice Currency is required' }),
        })
        .int()
        .nullable()
        // if currency is null, use 0 to indicate multiple currencies
        .transform((currency) => currency ?? 0),
    poDelivery: PODeliverySchema.nullable(),
    vatInformation: VATSchema.nullable(),
});

const YourBusinessSchema = z
    .object({
        businessName: z
            .string({ required_error: 'Business Name is required' })
            .trim()
            .min(1, 'Business Name is required'),
        // Billing Address
        address1: z.string({ required_error: 'Address 1 is required' }).trim().min(1, 'Address 1 is required'),
        address2: z
            .string()
            .trim()
            .optional()
            .nullable()
            .transform((v) => v || undefined),
        address3: z
            .string()
            .trim()
            .optional()
            .nullable()
            .transform((v) => v || undefined),
        country: z
            .string({ required_error: 'Country is required', invalid_type_error: 'Country is required' })
            .trim()
            .min(1, 'Country is required'),
        countryHasStates: z.boolean().default(false), // no country selected initially
        state: z
            .string({
                invalid_type_error: 'State/Province/Region is required',
            })
            .trim()
            .min(1, 'State/Province/Region is required')
            .optional()
            .or(z.literal('')),
        city: z.string({ required_error: 'City is required' }).trim().min(1, 'City is required'),
        postalCode: z
            .string({ required_error: 'Zip/Postal Code is required' })
            .trim()
            .min(1, 'Zip/Postal Code is required'),
        // Other Info
        phone: z
            .string({ required_error: 'Phone is required' })
            .trim()
            .min(1, 'Phone is required')
            .regex(CharSetForPhoneAndFax, 'Phone includes invalid characters'),
        fax: z
            .union([
                z.string().regex(CharSetForPhoneAndFax, 'Fax includes invalid characters').trim(),
                z.string().length(0),
            ])
            .optional()
            .transform((v) => v || undefined),
        dunsNumber: z
            .union([
                z
                    .string()
                    .regex(CharSetForDUNSNumber, 'D-U-N-S number includes invalid characters')
                    .trim()
                    .min(9, 'At least 9 digits are required'),
                z.string().length(0),
            ])
            .optional()
            .transform((v) => v || undefined),
    })
    .superRefine(({ country, countryHasStates, state }, ctx) => {
        if (country && countryHasStates && !state) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'State/Province/Region is required',
                path: ['state'],
            });
        }
    });

export const SupplierSignUpSchema = z.object({
    accountAndContact: AccountAndContactSchema,
    customer: YourCustomerSchema,
    business: YourBusinessSchema,
});

export type SupplierSignUpData = z.infer<typeof SupplierSignUpSchema>;
